import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AlertCircle, Loader } from 'lucide-react'
import { useAuthStore } from '../services/authStore'
import { useTranslation } from '../contexts/TranslationContext'
import ThemeToggle from '../components/theme/ThemeToggle'
import brandLogo from '../assets/branding/savinfy-receipts-logo.png'

export default function Register() {
  const navigate = useNavigate()
  const { register, isLoading, error, clearError } = useAuthStore()
  const { t } = useTranslation()
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  })
  const location = useLocation()
  const [redirectTarget, setRedirectTarget] = useState('/')
  const [validationErrors, setValidationErrors] = useState({})

  const validateForm = () => {
    const errors = {}
    
    if (!formData.name) {
      errors.name = t('Name is required', 'Name is required')
    } else if (formData.name.length < 2) {
      errors.name = t('Name must be at least 2 characters', 'Name must be at least 2 characters')
    }
    
    if (!formData.email) {
      errors.email = t('Email is required', 'Email is required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('Please enter a valid email', 'Please enter a valid email')
    }
    
    if (!formData.password) {
      errors.password = t('Password is required', 'Password is required')
    } else if (formData.password.length < 8) {
      errors.password = t('Password must be at least 8 characters', 'Password must be at least 8 characters')
    }
    
    if (!formData.passwordConfirmation) {
      errors.passwordConfirmation = t('Please confirm your password', 'Please confirm your password')
    } else if (formData.password !== formData.passwordConfirmation) {
      errors.passwordConfirmation = t('Passwords do not match', 'Passwords do not match')
    }
    
    return errors
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
    // Clear field error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
      }))
    }
    clearError()
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setRedirectTarget(params.get('redirect') || '/')
  }, [location.search])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    try {
      await register(formData.name, formData.email, formData.password, formData.passwordConfirmation)
      localStorage.setItem('post_verify_redirect', redirectTarget)
      navigate('/login')
    } catch (err) {
      // Error is handled by the store
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-surface-elevated text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <ThemeToggle />
          </div>
          <img src={brandLogo} alt="Savinfy Receipts" className="mx-auto mb-4 h-14 w-auto" />
          <h1 data-testid="brand-wordmark" className="text-4xl font-bold text-foreground mb-2">
            {t('Receipt Scanner', 'Savinfy Receipts')}
          </h1>
          <p className="text-muted-foreground">{t('Start managing your receipts today', 'Start managing your receipts today')}</p>
        </div>

        {/* Card */}
        <div className="bg-surface border border-outline rounded-lg shadow-lg p-8 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{t('Create Account', 'Create Account')}</h2>
            <p className="text-muted-foreground text-sm mt-1">{t('Sign up to get started', 'Sign up to get started')}</p>
            <p className="text-muted-foreground text-xs mt-2">
              Create an account to use the web dashboard, cloud storage, backup, and sync. You can use the mobile app without an account for device-only receipt storage.
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                {t('Full Name', 'Full Name')}
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('John Doe', 'John Doe')}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.name ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.name && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                {t('Email Address', 'Email Address')}
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('you@example.com', 'you@example.com')}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.email ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.email && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                {t('Password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="вЂўвЂўвЂўвЂўвЂўвЂўвЂўвЂў"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.password ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.password && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.password}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">{t('At least 8 characters', 'At least 8 characters')}</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="passwordConfirmation" className="block text-sm font-medium text-foreground mb-2">
                {t('Confirm Password', 'Confirm Password')}
              </label>
              <input
                id="passwordConfirmation"
                type="password"
                name="passwordConfirmation"
                value={formData.passwordConfirmation}
                onChange={handleChange}
                placeholder="вЂўвЂўвЂўвЂўвЂўвЂўвЂўвЂў"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.passwordConfirmation ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.passwordConfirmation && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.passwordConfirmation}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  {t('Creating account...', 'Creating account...')}
                </>
              ) : (
                t('Create Account', 'Create Account')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-outline"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-muted-foreground">{t('or', 'or')}</span>
            </div>
          </div>

          {/* Login Link */}
          <p className="text-center text-muted-foreground text-sm">
            {t('Already have an account?', 'Already have an account?')}{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-semibold">
              {t('Sign In', 'Sign In')}
            </Link>
          </p>
        </div>

        {/* Terms */}
        <p className="text-center text-muted-foreground text-xs mt-6">
          {t('By signing up, you agree to our Terms of Service and Privacy Policy', 'By signing up, you agree to our Terms of Service and Privacy Policy')}
        </p>
      </div>
    </div>
  )
}

